import React from "react";
import {
  CONTRACT_KINDS,
  CONTRACT_KIND_LABELS,
  type ContractKind,
  kindHasJurisdiction,
} from "@/services/docuseal";
import SaveButton from "@/components/documents/SaveButton";
import JurisdictionSelect from "@/components/documents/JurisdictionSelect";
import TrialLocked from "@/components/trial/TrialLocked";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface DocumentConfigCardProps {
  name: string;
  onNameChange: (name: string) => void;
  /** null: not picked yet (the New page); Save waits for a choice. */
  kind: ContractKind | null;
  onKindChange: (kind: ContractKind) => void;
  jurisdiction: string | null;
  onJurisdictionChange: (code: string | null) => void;
  enabled: boolean;
  enableDisabled: boolean;
  onEnabledChange: (checked: boolean) => void;
  /** null when there is no enabled row, so no default to make. */
  isDefault: boolean | null;
  makeDefaultDisabled: boolean;
  onMakeDefault: () => void;
  saveDisabled: boolean;
  /** Unsaved changes: Save is highlighted. Otherwise it stays muted. */
  dirty?: boolean;
  /** The last save went through and nothing has changed since: Save reads "Saved". */
  saved?: boolean;
  saving: boolean;
  onSave: () => void;
  /** Sits after Save in the header (the Edit page's delete, the New page's cancel). */
  headerAction?: React.ReactNode;
  /** Rendered under the fields (the New page's Open builder). */
  children?: React.ReactNode;
}

/** The Configuration card shared by the New and Edit pages. */
const DocumentConfigCard: React.FC<DocumentConfigCardProps> = ({
  name,
  onNameChange,
  kind,
  onKindChange,
  jurisdiction,
  onJurisdictionChange,
  enabled,
  enableDisabled,
  onEnabledChange,
  isDefault,
  makeDefaultDisabled,
  onMakeDefault,
  saveDisabled,
  dirty = false,
  saved = false,
  saving,
  onSave,
  headerAction,
  children,
}) => (
  <Card>
    <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <CardTitle>Configuration</CardTitle>
      <TrialLocked lock="contractDocuments" inline>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="enable">Enable</Label>
            <Switch
              id="enable"
              checked={enabled}
              disabled={enableDisabled}
              onCheckedChange={onEnabledChange}
            />
          </div>
          {isDefault != null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={makeDefaultDisabled}
              onClick={onMakeDefault}
            >
              {isDefault ? "Default" : "Make default"}
            </Button>
          )}
          <SaveButton
            dirty={dirty}
            saving={saving}
            saved={saved}
            disabled={saveDisabled}
            onClick={onSave}
          />
          {headerAction}
        </div>
      </TrialLocked>
    </CardHeader>
    <CardContent className="space-y-4">
      {/* Fixed three columns so the jurisdiction picker appearing never reflows the row. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="template-name">Template name</Label>
          <Input
            id="template-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder="Standard MCA"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="document-kind">Type</Label>
          <Select
            value={kind ?? undefined}
            // The jurisdiction stays in state while hidden so switching back restores it;
            // saves and registrations send it through `laneJurisdiction`.
            onValueChange={(value) => onKindChange(value as ContractKind)}
          >
            <SelectTrigger id="document-kind">
              <SelectValue placeholder="Choose a type" />
            </SelectTrigger>
            <SelectContent>
              {CONTRACT_KINDS.map((option) => (
                <SelectItem key={option} value={option}>
                  {CONTRACT_KIND_LABELS[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {kind && kindHasJurisdiction(kind) && (
          <div className="space-y-1.5">
            <Label htmlFor="document-jurisdiction">Jurisdiction</Label>
            <JurisdictionSelect
              id="document-jurisdiction"
              value={jurisdiction}
              onChange={onJurisdictionChange}
            />
          </div>
        )}
      </div>
      {children}
    </CardContent>
  </Card>
);

export default DocumentConfigCard;
